import axios from 'axios'
import store from '@/store'
import { getToken, removeToken, getRefreshToken, setToken, setRefreshToken, removeRefreshToken } from '@/utils/cookieUtil'
import { syncCurrentAccount } from '@/utils/accountStore'
import { getDeviceHeaders } from '@/utils/deviceHeaders'
import {
    TIMEOUT,
    ERROR_KIND,
    classifyRequestError,
    isIdempotentMethod,
    shouldRetry,
    retryDelay,
    wait,
    notifyNetworkError,
    decorateError
} from '@/utils/networkError'
import { Notification } from 'element-ui'
import Vue from 'vue'
import JSONBig from 'json-bigint'

/**
 * 旧口径的账号封禁业务码，与服务端 ResultCode.DISABLE_ACCOUNT 对应（登录即拦截、清掉登录态）。
 * 新版服务端不再返回，保留给还没升级的服务端。
 */
export const BAN_CODE = -12

/**
 * 账号封禁中、本次是受限操作，与服务端 ResultCode.BAN_RESTRICTED 对应。
 * 封禁期间账号照常登录、照常浏览（对齐小红书），只有发帖、评论、私信、点赞收藏、关注、
 * 改资料等操作返回这个码。响应 data 为封禁详情，restrictedAction 是这次被拦的功能。
 */
export const BAN_RESTRICTED_CODE = -16

// 是否正在刷新 token 的标志
let isRefreshing = false
// 存储待重试的请求队列
let requestsQueue = []
// 是否已经显示登录框的标志（防止重复弹出）
let isShowingLoginDialog = false
// 登录框显示的时间戳
let loginDialogShowTime = 0
// 是否正在退出登录的标志
let isLoggingOut = false
// create an axios instance
const service = axios.create({
    baseURL: process.env.VUE_APP_BASE_API,
    // 实例默认取读请求档位；写请求在请求拦截器里上调到 TIMEOUT.WRITE
    timeout: TIMEOUT.READ
})

// request interceptor
service.interceptors.request.use(

    config => {
        // 如果正在退出登录且当前有token（用户主动退出场景），取消新请求
        // 未登录用户不受此限制
        if (isLoggingOut && getToken()) {
            return Promise.reject(new Error('__SILENT_AUTH__'))
        }
        
        // if (config.url != '/oauth/wechat/is_login' && config.url != '/v1/im/chat' && config.url != '/v1/article/search' && config.url != '/v1/tag/') {
        //     window.vm.$bus.$emit('showLoading');
        // }
        //do something before request is sent
        let token = getToken()
        if (token != null) {
            // let each request carry token
            // ['X-Token'] is a custom headers key
            // please modify it according to the actual situation
            config.headers.Authorization = token
        }

        // 设备指纹请求头，用于安全合规溯源（登录日志 / 设备档案）
        Object.assign(config.headers, getDeviceHeaders())

        // 写请求放宽超时：重发有副作用（重复发帖 / 重复点赞），宁可多等也要让它走完。
        // 判等 TIMEOUT.READ 是为了识别「调用方没有自己指定 timeout」——
        // axios 会把实例默认值合并进 config，这里拿不到「是否显式传入」的信息，
        // 值仍等于默认档位就说明没被调用方覆盖过。
        if (config.timeout === TIMEOUT.READ && !isIdempotentMethod(config.method)) {
            config.timeout = TIMEOUT.WRITE
        }

        return config
    },
    error => {
        // do something with request error
        console.log(error) // for debug
        return Promise.reject(error)
    }
)

// 刷新 token 的函数
async function handleRefreshToken() {
    // 如果正在退出登录，直接静默拒绝
    if (isLoggingOut) {
        return Promise.reject(new Error('__SILENT_AUTH__'))
    }
    
    const refreshToken = getRefreshToken()
    
    if (!refreshToken) {
        // 没有 refreshToken
        // 如果也没有 token，说明用户未登录，静默处理只弹登录框
        const hasToken = getToken()
        if (!hasToken) {
            removeToken()
            removeRefreshToken()
            sessionStorage.removeItem("user")
            store.commit('userStore/setUserInfo', {})
            store.commit('systemStore/setLoginFlag', true)
            return Promise.reject(new Error('__SILENT_AUTH__'))
        }
        // 有 token 但无 refreshToken，弹出登录框
        handleLogout()
        return Promise.reject(new Error('__SILENT_AUTH__'))
    }

    try {
        // 调用刷新 token 接口 - 使用完整URL避免baseURL重复
        const response = await axios.post(
            `${process.env.VUE_APP_BASE_API}/v1/user/refreshToken`,
            { 
                refreshToken,
                device: 'PC'
            },
            {
                // token 续期同样会产生一条登录记录，需带上设备指纹
                headers: getDeviceHeaders(),
                // 必须显式指定超时。这里用的是全局 axios 而不是 service 实例，
                // 拿不到实例上的 timeout，继承的是 axios 默认值 0（永不超时）。
                // 而调用它之前 isRefreshing 已经置位、下面 .finally 里才复位 ——
                // 弱网下这个请求一旦挂住就永远不会 settle，isRefreshing 永远是 true，
                // requestsQueue 里所有等着重放的请求全部无限期挂起：
                // 页面一直转圈，既不超时也不报错，用户完全拿不到反馈。
                timeout: TIMEOUT.AUTH
            }
        )

        // 接口返回 500 或 code 不为 200 都视为刷新失败
        if (response.status === 500 || response.data.code !== 200 || !response.data.data) {
            throw new Error('刷新 token 失败')
        }

        const { token, refreshToken: newRefreshToken } = response.data.data
        
        // 保存新的 token 和 refreshToken
        setToken(token)
        setRefreshToken(newRefreshToken)
        
        // 更新用户信息（与登录接口返回结构一致）
        store.commit('userStore/setUserInfo', response.data.data)

        // 把续期后的凭证同步进多账号列表快照。
        // 不同步的话，列表里存的还是那条已经过期的 token，下次「切换账号」
        // 切过去会先吃一串 401 才靠 refreshToken 自愈。
        syncCurrentAccount()

        return token
    } catch (error) {
        const classified = classifyRequestError(error)

        // 刷新请求本身是网络层失败（超时 / 断网 / 5xx）时，refreshToken 并没有失效。
        // 这时候按"刷新失败"清登录态，等于因为一次网络抖动把用户踢下线，
        // 顺手还把那条仍然可用的 refreshToken 一起丢掉，恢复网络也回不来了。
        // 保留登录态，只把网络问题告诉用户，让这次请求失败掉、由用户重试。
        if (classified.isRecoverable) {
            console.warn('刷新 token 遇到网络问题，保留登录态:', classified.kind)
            notifyNetworkError(classified)
            throw decorateError(new Error(classified.message), classified)
        }

        console.error('刷新 token 失败:', error)
        // 服务端明确拒绝（refreshToken 过期 / 无效）才需要用户重新登录
        handleLogout()
        throw new Error('__SILENT_AUTH__')
    }
}

// 退出登录处理 - 只弹出登录框，不显示任何文案
function handleLogout() {
    // 防止在短时间内（3秒）重复弹出登录框
    const now = Date.now()
    if (isShowingLoginDialog && (now - loginDialogShowTime) < 3000) {
        return
    }
    
    // 只有当前有 token 时才设置退出标志（用户主动退出场景）
    // 无 token 说明用户本来就没登录，无需阻止后续请求
    const hasToken = getToken()
    if (hasToken) {
        isLoggingOut = true
    }
    
    isShowingLoginDialog = true
    loginDialogShowTime = now
    
    removeToken()
    removeRefreshToken()
    sessionStorage.removeItem("user")
    store.commit('userStore/setUserInfo', {})
    store.commit('systemStore/setLoginFlag', true)
    
    // 未登录时只弹出登录框，不显示额外提示文案
    
    // 3秒后重置标志，允许再次弹出
    setTimeout(() => {
        isShowingLoginDialog = false
        isLoggingOut = false
    }, 3000)
}

// 导出设置退出登录状态的函数，供外部调用
export function setLoggingOut(value) {
    isLoggingOut = value
}

/**
 * 账号被封禁的统一处理（服务端业务码 -12）。
 *
 * 两种触发场景：
 *   1) 登录时被拦截 —— 用户本来就没有登录态；
 *   2) 使用过程中被后台封禁 —— 服务端的请求级拦截器返回 -12，此时必须把本地登录态
 *      彻底清掉（含 refreshToken），否则页面还会拿旧 token 继续发请求、反复弹窗。
 *
 * 与 401 的区别：401 是"请重新登录"，弹登录框即可；-12 是"这个账号不能用了"，
 * 要展示处罚原因、期限与申诉入口，弹登录框反而会让用户一直卡在登录失败的循环里。
 */
function handleAccountBanned(banInfo) {
    isLoggingOut = false
    isRefreshing = false
    requestsQueue = []

    removeToken()
    removeRefreshToken()
    sessionStorage.removeItem('user')
    store.commit('userStore/setUserInfo', {})
    store.commit('systemStore/showBanDialog', banInfo)
}

export { handleAccountBanned }

// response interceptor
service.interceptors.response.use(
    /**
     * If you want to get http information such as headers or status
     * Please return  response => response
     */

    /**
     * Determine the request status by custom code
     * Here is just an example
     * You can also judge the status by HTTP Status Code
     */
    response => {
        // console.log('响应拦截器的response--->', response)
        // window.vm.$bus.$emit('hideLoading');
        const res = response.data
        // store.commit('SET_LOADING',false);
        // if the custom code is not 20000, it is judged as an error.

        if (res.code !== 200) {
            // 账号被封禁：优先于 401 处理，直接弹封禁详情弹窗，不走刷新 token / 登录框逻辑
            if (res.code === BAN_CODE) {
                handleAccountBanned(res.data)
                return Promise.reject(new Error('__ACCOUNT_BANNED__'))
            }
            // 封禁中的受限操作：不清登录态，弹封禁说明（受限功能 + 申诉入口）。
            // 复用 __ACCOUNT_BANNED__ 哨兵：各页面的 catch 和网络错误归类都认得它，不会再叠一条「操作失败」
            if (res.code === BAN_RESTRICTED_CODE) {
                store.commit('systemStore/showBanDialog', res.data || {})
                return Promise.reject(new Error('__ACCOUNT_BANNED__'))
            }
            if (res.code == 401) {
                // 如果正在退出登录，直接静默拒绝，不弹登录框
                if (isLoggingOut) {
                    return Promise.reject(new Error('__SILENT_AUTH__'))
                }
                
                // token 失效，尝试刷新
                const config = response.config
                
                // 如果是刷新 token 的请求失败，或者配置了跳过 token 刷新，直接退出登录
                if (config.skipTokenRefresh || config.url.includes('/refreshToken')) {
                    handleLogout()
                    return Promise.reject(new Error('__SILENT_AUTH__'))
                }

                // 如果正在刷新 token，将请求加入队列
                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        requestsQueue.push({ config, resolve, reject })
                    })
                }

                // 开始刷新 token
                isRefreshing = true

                return handleRefreshToken()
                    .then(newToken => {
                        // 刷新成功，重试当前请求
                        config.headers.Authorization = newToken
                        
                        // 重试队列中的所有请求
                        requestsQueue.forEach(({ config, resolve, reject }) => {
                            config.headers.Authorization = newToken
                            service(config).then(resolve).catch(reject)
                        })
                        
                        // 清空队列
                        requestsQueue = []
                        
                        // 重试当前请求
                        return service(config)
                    })
                    .catch(error => {
                        // 刷新失败，拒绝队列中的所有请求
                        requestsQueue.forEach(({ reject }) => {
                            reject(error)
                        })
                        requestsQueue = []
                        return Promise.reject(error)
                    })
                    .finally(() => {
                        isRefreshing = false
                    })
            }
            //如果是校验微信登录是否授权的接口 则不进行错误输出
            if (response.config.url !== "/oauth/wechat/is_login") {
                window.vm.$toast.error(res.message)
            }


            return Promise.reject(new Error(res.message || 'Error'))
        } else {
            return res
        }
    },
    /**
     * 网络层失败的统一出口。
     *
     * 这里以前只是把 error 原样 reject —— 超时、断网、DNS 失败、5xx 在调用方眼里
     * 和业务报错完全一样，用户在弱网下得不到任何反馈。现在做三件事：
     *   1) 归类；2) 对幂等请求的快速失败做退避重试；3) 给出统一文案并把结论挂到 error 上。
     */
    async error => {
        const classified = classifyRequestError(error)

        // 认证 / 封禁哨兵（提示已由登录框、封禁弹窗承担）与主动取消（预期行为），一律静默
        if (classified.kind === ERROR_KIND.SILENT || classified.kind === ERROR_KIND.CANCELED) {
            return Promise.reject(error)
        }

        const config = error.config

        if (shouldRetry(config, classified)) {
            config.__retryCount = (config.__retryCount || 0) + 1
            await wait(retryDelay(config.__retryCount))
            // 重新走完整链路（含请求拦截器），这样重试能用上可能已经刷新过的 token
            return service(config)
        }

        decorateError(error, classified)

        // 已经自己接管失败反馈的调用方可传 skipNetworkErrorToast 关掉统一提示
        if (!(config && config.skipNetworkErrorToast)) {
            notifyNetworkError(classified)
        }

        return Promise.reject(error)
    }
)

export default service